import { QA_ALERT_CATEGORY_MAP, type AlertCategoryKey } from '@/shared/constants/qaAlertConfig'

export interface ResolveActivityFeedAlertCategoryParams {
  eventType: string
  isBackdated: boolean
  summaryText?: string | null
}

/**
 * Mirrors Rails `reports.maintenance_logs.activity_feed.summary.failure`
 * ("… recorded failure for …"). Feed items do not expose status codes, so
 * coloring must parse the server-built English summary until a structured
 * field is added. Keep the phrase aligned with config/locales/en.yml.
 */
const TASK_LOG_FAILURE_SUMMARY_PATTERN = /recorded failure for/i

/**
 * Maps a Maintenance Logs activity-feed item to a shared alert category.
 *
 * Product (locked for UAT 298 / REBUILD-261):
 * - Successful `task_log` → neutral (null).
 * - NPT logged as `task_log` ("logged No Testing for …") → neutral. NPT is not
 *   non-compliant; do not map it to `nonCompliant`.
 * - `review_action` → `nonCompliant` (red). All NC review-workflow feed items
 *   use this event type, including reviews that record NPT — the review action
 *   itself is the alert signal, distinct from a plain NPT task_log.
 * - `task_deactivation` → `correctiveActions` (slate; no Frequency Change key).
 */
export const resolveActivityFeedAlertCategory = ({
  eventType,
  isBackdated,
  summaryText,
}: ResolveActivityFeedAlertCategoryParams): AlertCategoryKey | null => {
  if (isBackdated) return 'dateChanges'

  switch (eventType) {
    case 'service_report':
      return 'serviceReports'
    case 'status_change':
      return 'statusChanges'
    case 'department_move':
      return 'departmentMoved'
    case 'review_action':
      return 'nonCompliant'
    case 'task_deactivation':
      return 'correctiveActions'
    case 'task_log':
      return resolveTaskLogAlertCategory(summaryText)
    default:
      return null
  }
}

export const activityFeedBadgeColorClass = (
  params: ResolveActivityFeedAlertCategoryParams,
): string => {
  const category = resolveActivityFeedAlertCategory(params)
  if (!category) return ''
  return QA_ALERT_CATEGORY_MAP[category].badgeColorClass
}

const resolveTaskLogAlertCategory = (summaryText?: string | null): AlertCategoryKey | null => {
  if (!summaryText) return null
  if (TASK_LOG_FAILURE_SUMMARY_PATTERN.test(summaryText)) return 'failures'
  return null
}
